import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faMinus } from '@fortawesome/free-solid-svg-icons'
import { formatValue } from '../review/format'

// No date on record means "never" to the analytic, so say so instead of an em dash.
const describeValue = (criterion) => {
  if (criterion.value === null || criterion.value === undefined) return 'None on record'
  return formatValue(criterion.value, 'date')
}

const describeAge = (criterion) => {
  if (criterion.months_ago === null || criterion.months_ago === undefined) return null
  if (criterion.months_ago < 1) return 'this month'
  if (criterion.months_ago === 1) return '1 month ago'
  return `${criterion.months_ago} months ago`
}

const Card = ({ children }) => (
  <div
    style={{
      background: 'var(--surface-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-sm)',
      overflow: 'hidden'
    }}
  >
    {children}
  </div>
)

Card.propTypes = { children: PropTypes.node }

const CELL = {
  padding: '9px 14px',
  fontSize: 'var(--text-sm)',
  color: 'var(--text-body)',
  textAlign: 'left',
  verticalAlign: 'top'
}

const CriterionRow = ({ criterion, first }) => {
  const met = criterion.met
  const border = first ? 'none' : '1px solid var(--border-subtle)'

  return (
    <tr>
      <td style={{ ...CELL, borderTop: border, width: 30, paddingRight: 0 }}>
        <span
          aria-label={met ? 'Met' : 'Not met'}
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: met ? 'var(--rust-100)' : 'var(--navy-50)',
            color: met ? 'var(--rust-700)' : 'var(--text-subtle)'
          }}
        >
          <FontAwesomeIcon icon={met ? faCheck : faMinus} style={{ width: 9, height: 9 }} />
        </span>
      </td>
      <td style={{ ...CELL, borderTop: border, fontWeight: 700, color: 'var(--text-strong)', whiteSpace: 'nowrap' }}>
        {criterion.label}
      </td>
      <td style={{ ...CELL, borderTop: border, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
        {describeValue(criterion)}
      </td>
      <td style={{ ...CELL, borderTop: border, color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>
        {describeAge(criterion)}
      </td>
      <td style={{ ...CELL, borderTop: border, color: 'var(--text-muted)', fontSize: 'var(--text-xs)', width: '100%' }}>
        {criterion.note}
      </td>
    </tr>
  )
}

CriterionRow.propTypes = { criterion: PropTypes.object.isRequired, first: PropTypes.bool }

const SectionHeading = ({ children, aside }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 8,
      flexWrap: 'wrap'
    }}
  >
    <h3
      style={{
        margin: 0,
        fontSize: 'var(--text-xs)',
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '.08em',
        color: 'var(--text-muted)'
      }}
    >
      {children}
    </h3>
    {aside}
  </div>
)

SectionHeading.propTypes = { children: PropTypes.node, aside: PropTypes.node }

const EvidenceBody = ({ window: evidenceWindow, criteria, context }) => (
  <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'grid', gap: 14 }}>
    <section>
      <SectionHeading
        aside={evidenceWindow && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-subtle)' }}>
            {evidenceWindow.label}: {evidenceWindow.months} months — anything before{' '}
            <span style={{ fontFamily: 'var(--font-mono)' }}>
              {formatValue(evidenceWindow.cutoff, 'date')}
            </span>{' '}
            counts as quiet
          </span>
        )}
      >
        What the analytic tested
      </SectionHeading>

      <Card>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: 0 }}>
          <tbody>
            {criteria.map((criterion, index) => (
              <CriterionRow key={criterion.label} criterion={criterion} first={index === 0} />
            ))}
          </tbody>
        </table>
      </Card>
    </section>
    {context.length > 0 && (
      <section>
        <SectionHeading>Vendor record</SectionHeading>
        <Card>
          <dl
            style={{
              margin: 0,
              padding: '16px 18px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              columnGap: 24,
              rowGap: 16
            }}
          >
            {context.map((entry) => (
              <div key={entry.k}>
                <dt
                  style={{
                    fontSize: 'var(--text-2xs)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em',
                    color: 'var(--text-subtle)'
                  }}
                >
                  {entry.k}
                </dt>
                <dd
                  style={{
                    margin: 0,
                    marginTop: 2,
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-body)',
                    fontFamily: entry.kind === 'text' ? 'inherit' : 'var(--font-mono)',
                    overflowWrap: 'anywhere'
                  }}
                >
                  {formatValue(entry.v, entry.kind)}
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      </section>
    )}
  </div>
)

EvidenceBody.propTypes = {
  window: PropTypes.object,
  criteria: PropTypes.array.isRequired,
  context: PropTypes.array.isRequired
}

export default EvidenceBody
